import { alpha } from '@mui/material';
import { OrderStatus, OrderTransaction, QuoteType } from '@orbis-systems/orbis-api-middleware';
import { ColorGenerator } from './colorGenerator';
import { getCurrentTheme } from '@/themes';
import {
    BankLinkStatus,
    ConsensusFormatOptions,
    CorporateActionCategory,
    InverseOrLeverageOptions,
    ModelRebalanceCategory,
    MoneyTransferStatus,
    OptionOrderTransaction,
    PlaidSubType,
    Scope,
} from '../constants/lookups';

const colorGenerator = new ColorGenerator([
    '#3fbdd6',
    '#3f97f6',
    '#90c341',
    '#f5550f',
    '#4452b8',
    '#d50000',
    '#9c27b0',
    '#2e7d32',
    '#00bfa5',
    '#ffd600',
]);

export const getNextColor = colorGenerator.getNextColor;

export const getColorByKey = colorGenerator.getColorByKey;

export const getConstantColor = (constant: string) => {
    const theme = getCurrentTheme();

    switch (constant) {
        // Statuses
        case 'Complete':
        case 'Completed':
            return theme.palette.common.green;
        case 'Pending':
            return theme.palette.common.blue;
        case 'Rejected':
            return theme.palette.common.red;
        case 'Canceled':
            return theme.palette.common.purple;
        case 'Approved':
            return theme.palette.common.green;

        case QuoteType.Realtime:
        case QuoteType.RealtimeSnapshot:
            return '#0065b6';
        case QuoteType.Delayed:
            return '#212121';
        case QuoteType.CBOE1:
            return '#560192';
        case QuoteType.BATS:
            return '#630387';

        // corporate action category
        case CorporateActionCategory.Pending:
            return theme.palette.common.lightGreen;
        case CorporateActionCategory.Recent:
            return theme.palette.primary.main;
        case CorporateActionCategory.IntermediateTerm:
            return theme.palette.common.darkGold;
        case CorporateActionCategory.LongTerm:
            return theme.palette.common.orange;

        //consensus
        case 'Strong Buy':
        case 'Moderate Buy':
        case 'Buy':
            return theme.palette.consensus.buy;
        case 'Strong Sell':
        case 'Moderate Sell':
        case 'Sell':
            return theme.palette.consensus.sell;
        case 'Neutral':
        case 'Hold':
            return theme.palette.consensus.hold;

        //transactions
        case OrderTransaction.Sell:
        case OrderTransaction.ShortSell:
        case OptionOrderTransaction.SellToOpen:
        case OptionOrderTransaction.SellToClose:
            return theme.palette.common.red;
        case OrderTransaction.Buy:
        case OrderTransaction.BuyToCover:
        case OptionOrderTransaction.BuyToOpen:
        case OptionOrderTransaction.BuyToClose:
            return theme.palette.common.green;

        case 'INCOMING':
            return theme.palette.common.green;
        case 'OUTGOING':
            return theme.palette.common.red;

        case MoneyTransferStatus.Complete:
            return theme.palette.common.blue;
        case MoneyTransferStatus.Canceled:
        case MoneyTransferStatus.Rejected:
            return theme.palette.common.red;
        case BankLinkStatus.Pending:
            return theme.palette.common.blue;
        case BankLinkStatus.Approved:
            return theme.palette.common.green;
        case BankLinkStatus.Canceled:
        case BankLinkStatus.Rejected:
            return theme.palette.common.red;

        case PlaidSubType.Checking:
            return theme.palette.common.blue;
        case PlaidSubType.Savings:
            return theme.palette.common.green;

        // Model rebalance category
        case ModelRebalanceCategory.DefinedByPortfolio:
            return theme.palette.common.green;
        case ModelRebalanceCategory.MissingFromPortfolio:
            return theme.palette.common.orange;
        case ModelRebalanceCategory.NotDefinedByPortfolio:
            return theme.palette.common.red;

        default:
            return getColorByKey(constant, Scope.Constant);
    }
};

export const getConsensusColors = (constant: ConsensusFormatOptions) => {
    const theme = getCurrentTheme();
    switch (constant) {
        case ConsensusFormatOptions.StrongBuy:
        case ConsensusFormatOptions.StrongBuySpace:
            return theme.palette.consensus.strongBuy;
        case ConsensusFormatOptions.Buy:
            return theme.palette.consensus.buy;
        case ConsensusFormatOptions.StrongSell:
        case ConsensusFormatOptions.StrongSellSpace:
            return theme.palette.consensus.strongSell;
        case ConsensusFormatOptions.Sell:
            return theme.palette.consensus.sell;
        case ConsensusFormatOptions.Hold:
            return theme.palette.consensus.hold;
        case ConsensusFormatOptions.Neutral:
            return theme.palette.consensus.hold;
    }
};

export const getLeverageColors = (leverage: number, category: string) => {
    const theme = getCurrentTheme();
    if (
        (leverage > 1 && category.includes(InverseOrLeverageOptions.Inverse)) ||
        category.includes(InverseOrLeverageOptions.Misc)
    ) {
        return alpha(getCurrentTheme().palette.common.red, 0.5);
    } else if (
        leverage > 1 &&
        !(category.includes(InverseOrLeverageOptions.Inverse) || category.includes(InverseOrLeverageOptions.Misc))
    ) {
        return alpha(getCurrentTheme().palette.common.green, 0.5);
    } else if (
        (category.includes(InverseOrLeverageOptions.Inverse) || category.includes(InverseOrLeverageOptions.Misc)) &&
        leverage === 1
    ) {
        return alpha(getCurrentTheme().palette.common.red, 0.5);
    } else if (
        leverage === 1 &&
        !(category.includes(InverseOrLeverageOptions.Leverage) || category.includes(InverseOrLeverageOptions.Misc)) &&
        !(category.includes(InverseOrLeverageOptions.Inverse) || category.includes(InverseOrLeverageOptions.Misc))
    ) {
        return theme.palette.grey[600];
    } else {
        return theme.palette.grey[600];
    }
};

export const getOrderStatusColor = (status: OrderStatus | string) => {
    const theme = getCurrentTheme();

    switch (status) {
        case OrderStatus.Place:
        case OrderStatus.AcceptedAtTheExchange:
        case OrderStatus.WaitingForCancelReplace:
            return theme.palette.warning.main;
        case OrderStatus.PartiallyFilled:
            return theme.palette.info.main;
        case OrderStatus.Cancelled:
        case OrderStatus.WaitingForCancellation:
        case OrderStatus.Rejected:
            return theme.palette.error.main;
        case OrderStatus.Filled:
            return theme.palette.success.main;
        default:
            return theme.palette.text.secondary;
    }
};

export function getContrastTextColor(backgroundColor: string) {
    if (!backgroundColor) {
        return getCurrentTheme().palette.text.primary;
    }
    return getCurrentTheme().palette.getContrastText(alpha(backgroundColor, 1));
}
